import React, { useState, useEffect } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts';
import { useTheme } from '../../contexts/ThemeContext';
import { formatCurrency, formatCurrencyInput, processCurrencyInput, formatCurrencyOnBlur } from '../../utils/formatters';
import Modal from './Modal';
import logger from '../../utils/logger';
import { BarChart } from '@mui/icons-material';

// Remove MUI imports and replace with our own icons (using SVG)
const EditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const DeleteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const AddIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

// Add PieChartIcon component at the top with other icons
const PieChartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-4 h-4">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
  </svg>
);

const DONUT_SIZE = 80; // Reduced from 120
const DONUT_THICKNESS = 20; // Reduced from 24

// Member indicator styles
const getMemberIndicator = (memberName) => {
  if (!memberName) return null;
  const name = memberName.toLowerCase();
  
  if (name === 'household') {
    return {
      letter: 'H',
      color: '#9c27b0', // purple
      tooltip: 'Household Income'
    };
  }
  
  // Assuming first member is Thomas and second is Mary
  if (name.includes('thomas')) {
    return {
      letter: 'T',
      color: '#2196f3', // blue
      tooltip: memberName
    };
  }
  
  return {
    letter: 'M',
    color: '#4caf50', // green
    tooltip: memberName
  };
};

// Income type colors
const INCOME_COLORS = {
  '1099 Income': '#607d8b',
  'Rental Income': '#795548',
  'Retirement Account Distributions': '#ff9800',
  'Social Security': '#2196f3',
  'Pension': '#4caf50',
  'Royalty Income': '#9c27b0',
};

const IncomeSummaryCard = ({
  chartData = [],
  detailedIncomes = [],
  totalAnnualIncome = '$0',
  onShowDetails,
  incomeTypes = {},
  frequencies = [],
  onEditIncome,
  onDeleteIncome,
  onAddIncome,
  members = [],
  getIncomeTypeName,
}) => {
  const { isDarkMode } = useTheme();
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [isHovering, setIsHovering] = useState(false); // New state for hover tooltip

  // Enhanced debug logging
  useEffect(() => {
    logger.info('IncomeSummaryCard mounted with props:', {
      incomeTypesCount: Object.keys(incomeTypes || {}).length,
      frequenciesCount: (frequencies || []).length,
      detailedIncomesCount: detailedIncomes.length,
      chartDataCount: chartData.length,
      membersCount: members.length
    });

    // Detailed validation of income types
    if (Object.keys(incomeTypes || {}).length === 0) {
      logger.warn('No income types available in IncomeSummaryCard');
    } else {
      logger.info('Available income types:', 
        Object.entries(incomeTypes).map(([id, type]) => ({
          id,
          name: type.income_type_name
        }))
      );
    }

    // Detailed validation of frequencies
    if ((frequencies || []).length === 0) {
      logger.warn('No frequencies available in IncomeSummaryCard');
    } else {
      logger.info('Available frequencies:', 
        frequencies.map(freq => ({
          id: freq.frequency_id,
          name: freq.frequency_name,
          multiplier: freq.frequency_multiplier
        }))
      );
    }
  }, [incomeTypes, frequencies, detailedIncomes, chartData, members]);

  // Normalize incomeTypes to always be an object (moved inside component)
  const normalizedIncomeTypes = React.useMemo(() => {
    logger.info('Normalizing income types:', { rawTypes: incomeTypes });
    if (Array.isArray(incomeTypes)) {
      return incomeTypes.reduce((acc, type) => {
        if (type?.income_type_id) {
          acc[type.income_type_id] = type;
        }
        return acc;
      }, {});
    }
    return incomeTypes || {};
  }, [incomeTypes]);

  // Normalize frequencies to always be an array (moved inside component)
  const normalizedFrequencies = React.useMemo(() => {
    logger.info('Normalizing frequencies:', { rawFrequencies: frequencies });
    return Array.isArray(frequencies) ? frequencies : Object.values(frequencies || {});
  }, [frequencies]);

  // Debug logs for normalized data
  useEffect(() => {
    logger.info('Normalized data ready:', {
      incomeTypesCount: Object.keys(normalizedIncomeTypes).length,
      frequenciesCount: normalizedFrequencies.length
    });
  }, [normalizedIncomeTypes, normalizedFrequencies]);

  // Remove or modify the CustomTooltip component since we won't use it anymore

  // If no valid data, show empty state
  if (!chartData?.length) {
    return (
      <div className={`
        p-2 sm:p-3 w-full rounded border text-center
        ${isDarkMode 
          ? 'bg-neutral-800 border-neutral-700 text-gray-300' 
          : 'bg-white border-gray-200 text-gray-600'
        }
      `}>
        No income data available
      </div>
    );
  }

  return (
    <>
      {/* Keep card wrapped in overflow-hidden container */}
      <div className="overflow-hidden rounded-lg">
        <div 
          onClick={() => setShowEditPanel(true)}
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          className={`
            bg-white dark:bg-gray-800 text-gray-900 dark:text-white
            relative p-0
          `}
        >
          {/* Tooltip (green bubble, top right) */}
          <div className="absolute top-2 right-4 z-10 flex flex-col items-end pointer-events-none">
            {isHovering && (
              <div className="relative">
                <div className="bg-green-600 text-white text-xs font-semibold rounded-md px-3 py-1 shadow-lg">
                  View/Edit Income Details
                </div>
                <div className="absolute left-1/2 -bottom-2 transform -translate-x-1/2 w-3 h-3 overflow-hidden">
                  <svg className="w-3 h-3" viewBox="0 0 12 12">
                    <polygon points="6,12 12,0 0,0" fill="#16a34a" />
                  </svg>
                </div>
              </div>
            )}
          </div>

          {/* Main Content - Stacked Layout */}
          <div className="flex flex-col p-0">
            {/* Title with icon */}
            <div className="flex items-center gap-1 mb-2 px-2">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">Household Income Analysis</h2>
              <BarChart className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            {/* Donut Chart - Centered */}
            <div className="flex w-full justify-center mb-3 mt-2">
              <div className="relative w-[80px] h-[80px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={DONUT_SIZE / 2 - DONUT_THICKNESS}
                      outerRadius={DONUT_SIZE / 2}
                      paddingAngle={1}
                      dataKey="value"
                      startAngle={90}
                      endAngle={-270}
                    >
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.color || INCOME_COLORS[entry.name] || '#9e9e9e'}
                          fillOpacity={0.8}
                          stroke={isDarkMode ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.8)'}
                          strokeWidth={1}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                {/* Center Text */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
                  <div className={`text-[10px] block ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    Total
                  </div>
                  <div className="text-xs font-semibold">
                    {totalAnnualIncome}
                  </div>
                </div>
              </div>
            </div>

            {/* Dense Table */}
            <div className="overflow-visible">
              <table className="w-full table-auto">
                <thead>
                  <tr>
                    <th className="text-xs font-semibold py-0.5 px-1 text-left text-gray-500 dark:text-gray-300">Type</th>
                    <th className="text-xs font-semibold py-0.5 px-1 text-right text-gray-500 dark:text-gray-300">%</th>
                    <th className="text-xs font-semibold py-0.5 px-1 text-right text-gray-500 dark:text-gray-300">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {chartData.map((item) => {
                    const memberIndicators = item.details?.map(detail => getMemberIndicator(detail.memberName)) || [];
                    
                    return (
                      <tr 
                        key={item.name} 
                        className={`
                          border-b last:border-b-0 transition-colors duration-150
                          ${isDarkMode 
                            ? 'border-neutral-700 hover:bg-neutral-800' 
                            : 'border-gray-100 hover:bg-gray-50'
                          }
                        `}
                      >
                        <td className="py-0.5 px-1">
                          {memberIndicators.map((indicator, idx) => (
                            indicator && (
                              <span
                                key={idx}
                                title={indicator.tooltip}
                                className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] text-white mr-1"
                                style={{ backgroundColor: indicator.color }}
                              >
                                {indicator.letter}
                              </span>
                            )
                          ))}
                          <span
                            className="px-1.5 py-0.5 rounded-full text-[10px] font-medium text-white"
                            style={{
                              backgroundColor: item.color || INCOME_COLORS[item.name] || '#9e9e9e',
                              opacity: 0.85
                            }}
                          >
                            {item.name}
                          </span>
                        </td>
                        <td className="py-0.5 px-1 text-right text-xs font-medium text-gray-900 dark:text-white">
                          {item.percentage}%
                        </td>
                        <td className="py-0.5 px-1 text-right text-xs font-medium text-gray-900 dark:text-white">
                          {item.displayValue}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Edit Panel Modal */}
        <Modal
          isOpen={showEditPanel}
          onClose={() => setShowEditPanel(false)}
          maxWidth="2xl"
        >
          <div className="p-3">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-sm font-semibold">Income Sources</h2>
              <button
                onClick={() => setShowEditPanel(false)}
                className={`
                  px-2 py-1 text-xs rounded-md transition-colors
                  ${isDarkMode 
                    ? 'text-gray-300 hover:bg-neutral-700' 
                    : 'text-gray-700 hover:bg-gray-100'
                  }
                `}
              >
                Close
              </button>
            </div>

            {/* Table Container with Fixed Height */}
            <div className="overflow-y-auto max-h-[420px]">
              <table className="w-full table-auto">
                <thead className="sticky top-0 bg-inherit">
                  <tr className={isDarkMode ? 'border-neutral-700' : 'border-gray-200'}>
                    <th className="text-xs font-medium py-1 px-1 text-left">Member</th>
                    <th className="text-xs font-medium py-1 px-1 text-left">Type</th>
                    <th className="text-xs font-medium py-1 px-1 text-right">Amount</th>
                    <th className="text-xs font-medium py-1 px-1 text-right">Frequency</th>
                    <th className="text-xs font-medium py-1 px-1 text-right">Annual</th>
                    <th className="text-xs font-medium py-1 px-1 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(detailedIncomes || []).map((income) => {
                    if (!income) return null;
                    const member = (members || []).find((m) => m?.id === income?.member_id);
                    const annualAmount =
                      parseFloat(income?.gross_amount || income?.amount || 0) *
                      (income?.frequency_multiplier || 1);

                    return (
                      <tr 
                        key={income.id}
                        className="border-b last:border-b-0 dark:border-neutral-700 hover:bg-neutral-700/50"
                      >
                        {/* Member dropdown */}
                        <td className="py-1 px-1">
                          <select
                            value={income?.member_id || ""}
                            onChange={(e) => {
                              const updatedIncome = {
                                ...income,
                                member_id: e.target.value ? Number(e.target.value) : "",
                              };
                              onEditIncome?.(updatedIncome);
                            }}
                            className="block w-full px-1.5 py-0.5 border rounded text-xs bg-neutral-800 text-white border-neutral-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="">Household</option>
                            {members.map((member) => (
                              <option key={member.id} value={member.id}>
                                {member.name}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* Type dropdown */}
                        <td className="py-1 px-1">
                          <select
                            value={income?.income_type_id || ""}
                            onChange={(e) => {
                              const selectedType = normalizedIncomeTypes[e.target.value];
                              const updatedIncome = {
                                ...income,
                                income_type_id: selectedType ? Number(selectedType.income_type_id) : "",
                                income_type_name: selectedType ? selectedType.income_type_name : "",
                              };
                              onEditIncome?.(updatedIncome);
                            }}
                            className="block w-full px-1.5 py-0.5 border rounded text-xs bg-neutral-800 text-white border-neutral-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="">Select Type</option>
                            {Object.entries(normalizedIncomeTypes).map(([id, type]) => (
                              <option key={id} value={id}>
                                {type.income_type_name || "Unknown Type"}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* Amount input */}
                        <td className="py-1 px-1">
                          <input
                            type="text"
                            value={formatCurrencyInput(income?.gross_amount || income?.amount || "")}
                            onChange={(e) => {
                              const updatedIncome = {
                                ...income,
                                amount: processCurrencyInput(e.target.value),
                              };
                              onEditIncome?.(updatedIncome);
                            }}
                            onBlur={(e) => {
                              const updatedIncome = {
                                ...income,
                                amount: formatCurrencyOnBlur(e.target.value),
                              };
                              onEditIncome?.(updatedIncome);
                            }}
                            className="block w-full px-1.5 py-0.5 border rounded text-xs text-right bg-neutral-800 text-white border-neutral-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                        </td>

                        {/* Frequency dropdown */}
                        <td className="py-1 px-1">
                          <select
                            value={income?.frequency_id || ""}
                            onChange={(e) => {
                              const selectedFreq = normalizedFrequencies.find(
                                (f) => f?.frequency_id === Number(e.target.value)
                              );
                              const updatedIncome = {
                                ...income,
                                frequency_id: selectedFreq ? Number(selectedFreq.frequency_id) : "",
                                frequency_name: selectedFreq ? selectedFreq.frequency_name : "",
                                frequency_multiplier: selectedFreq ? Number(selectedFreq.frequency_multiplier) : 1,
                              };
                              onEditIncome?.(updatedIncome);
                            }}
                            className="block w-full px-1.5 py-0.5 border rounded text-xs bg-neutral-800 text-white border-neutral-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          >
                            <option value="">Select Frequency</option>
                            {normalizedFrequencies.map((freq) => (
                              <option 
                                key={freq?.frequency_id} 
                                value={freq?.frequency_id}
                              >
                                {freq?.frequency_name || "Unknown"}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* Annual amount */}
                        <td className="py-1 px-1 text-right text-xs">
                          {formatCurrency(annualAmount)}
                        </td>

                        {/* Actions */}
                        <td className="py-1 px-1">
                          <div className="flex justify-end">
                            <button
                              onClick={() => {
                                console.log('Delete button clicked for income:', income);
                                onDeleteIncome?.(income);
                              }}
                              className="w-7 h-7 rounded-full transition-colors text-red-400 hover:text-red-300 hover:bg-red-400/10 flex items-center justify-center"
                            >
                              <DeleteIcon />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Add Income Button */}
            <div className="mt-3 flex justify-center">
              <button
                onClick={() => {
                  console.log('Add Income button clicked');
                  console.log('onAddIncome exists?', !!onAddIncome);
                  onAddIncome?.();
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md
                  transition-colors duration-200
                  bg-blue-600 hover:bg-blue-700 text-white
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                  dark:focus:ring-offset-neutral-800"
              >
                <AddIcon />
                Add New Income
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </>
  );
};

export default IncomeSummaryCard; 